import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { BankReconciliationService } from '../../services/bankReconciliation.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { Account } from '../../interfaces/accounting.interface';
import { BankStatementLine } from '../../interfaces/bank/bankReconciliation.interface';
import { formatDate } from '../../utils/dateUtils';
import { leafAccounts } from '../../utils/postingAccounts';

const formatAmount = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  reconciliationId: string;
  line: BankStatementLine;
  onClose: () => void;
  onPosted: () => void;
}

/** Post a one-sided adjustment (bank charge, interest, correction, ...) for a statement line
 * that has no matching journal entry -- the contra account must be non-cash (CASH postingClass
 * is the reconciliation's own cash leg) and may be a global account (institutionId null). */
const AdjustmentDialog: React.FC<Props> = ({ reconciliationId, line, onClose, onPosted }) => {
  const toast = useToast();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    AccountService.getAllAccounts().then((data: Account[]) => {
      setAccounts(Array.isArray(data) ? leafAccounts(data).filter(a => a.postingClass !== 'CASH') : []);
    }).catch((e: any) => toast.showApiError(e, 'Failed to load accounts'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!accountId) return;
    try {
      setSaving(true);
      await BankReconciliationService.createAdjustment(reconciliationId, {
        bankStatementLineId: line.id,
        accountId,
        description: description.trim() || undefined,
      });
      toast.showSuccess('Adjustment posted');
      onPosted();
    } catch (e) {
      toast.showApiError(e, 'Failed to post adjustment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.adjustment.page' } as any}>
      <DialogTitle>Post Adjustment</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }} data-help-section="bank.adjustment.section.entry">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {formatDate(line.lineDate)} · {line.description || '—'}
          </Typography>
          <Typography variant="h6">{formatAmount(line.amount)}</Typography>
        </Box>
        <AccountAutocomplete data-help="bank.adjustment.field.account"
          accounts={accounts}
          value={accountId}
          onChange={id => setAccountId(id)}
          label="Contra Account"
          required
        />
        <TextField
          data-help="bank.adjustment.field.description"
          label="Description"
          fullWidth
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.adjustment.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.adjustment.post" variant="contained" disabled={saving || !accountId} onClick={handleSubmit}>Post</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustmentDialog;
